<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  patchMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

const COLUMNS = [
  { key: 'order_date', label: 'Tarih' },
  { key: 'order_number', label: 'Sipariş No' },
  { key: 'customer_name', label: 'Müşteri' },
  { key: 'country', label: 'Ülke' },
  { key: 'status', label: 'Statü' },
  { key: 'item_count', label: 'Adet' },
  { key: 'order_amount', label: 'Tutar' },
  { key: 'commission', label: 'Komisyon' },
  { key: 'discount', label: 'İndirim' },
  { key: 'forward_shipping', label: 'Gidiş Kargo' },
  { key: 'return_shipping', label: 'İade Kargo' },
  { key: 'platform_fee', label: 'Platform' },
  { key: 'return_amount', label: 'İade' },
  { key: 'net_amount', label: 'Net Tutar' },
  { key: 'product_cost', label: 'Ürün Maliyeti' },
  { key: 'net_profit', label: 'Net Kâr' },
  { key: 'payment_status', label: 'Ödeme' },
  { key: 'reconciled', label: 'Kontrol' },
];
const MONEY_KEYS = [
  'order_amount',
  'commission',
  'discount',
  'forward_shipping',
  'return_shipping',
  'platform_fee',
  'return_amount',
  'net_amount',
  'product_cost',
  'net_profit',
];

const filters = reactive({
  channel: 'all',
  status: 'all',
  payment: 'all',
  country: 'all',
  q: '',
  start: '',
  end: '',
});

const rows = ref([]);
const summary = ref({});
const pagination = ref({ page: 1, per_page: 50, total: 0, total_pages: 0 });
const meta = ref({});
const loading = ref(false);
const errorMsg = ref('');
const page = ref(1);
const selected = ref(null);
const selectedProducts = ref([]);
const loadingProducts = ref(false);
const exporting = ref(false);
const showHistory = ref(false);
const downloads = ref([]);

const queryString = () => {
  const p = new URLSearchParams();
  if (filters.channel !== 'all') p.set('channel', filters.channel);
  if (filters.status !== 'all') p.set('status', filters.status);
  if (filters.payment !== 'all') p.set('payment', filters.payment);
  if (filters.country !== 'all') p.set('country', filters.country);
  if (filters.q.trim()) p.set('q', filters.q.trim());
  if (filters.start) p.set('start', filters.start);
  if (filters.end) p.set('end', filters.end);
  return p;
};

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    const p = queryString();
    p.set('page', page.value);
    p.set('per_page', pagination.value.per_page);
    const data = await fetchMarketplaceJson(
      accountId,
      `trendyol_finances?${p.toString()}`
    );
    rows.value = data.finances;
    summary.value = data.summary;
    pagination.value = data.pagination;
    meta.value = data.meta;
  } catch (e) {
    errorMsg.value = 'Veriler yüklenemedi. Lütfen tekrar deneyin.';
  } finally {
    loading.value = false;
  }
};

let debounce = null;
watch(
  () => ({ ...filters }),
  () => {
    clearTimeout(debounce);
    debounce = setTimeout(() => {
      page.value = 1;
      load();
    }, 350);
  }
);

const goToPage = n => {
  if (n < 1 || n > pagination.value.total_pages) return;
  page.value = n;
  load();
};

const resetFilters = () => {
  filters.channel = 'all';
  filters.status = 'all';
  filters.payment = 'all';
  filters.country = 'all';
  filters.q = '';
  filters.start = '';
  filters.end = '';
};

const openDetail = async row => {
  selected.value = row;
  selectedProducts.value = [];
  loadingProducts.value = true;
  try {
    const data = await fetchMarketplaceJson(
      accountId,
      `trendyol_finances/${row.id}`
    );
    selectedProducts.value = data.products || [];
  } catch (e) {
    selectedProducts.value = [];
  } finally {
    loadingProducts.value = false;
  }
};

// --- Formatlama ---
const moneyFmt = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const money = v => `${moneyFmt.format(Number(v) || 0)} ₺`;
const fmtDate = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const netClass = v =>
  Number(v) < 0
    ? 'text-ruby-700 dark:text-ruby-400 font-semibold'
    : 'text-green-700 dark:text-green-400 font-semibold';

// Net kâr = net tutar − ürün maliyeti (iade/iptalde maliyet düşülmez, backend halleder).
// İade/iptalde kâr = net tutar (her zaman bilinir). Başarılı siparişte ise ancak
// tüm kalemlerin maliyeti biliniyorsa kâr güvenilir; eksikse gri "maliyet eksik".
const profitKnown = r => r.return_amount > 0 || r.cost_complete;

const statusClass = r => {
  if (r.cancelled)
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  if (r.returned)
    return 'bg-ruby-100 text-ruby-800 dark:bg-ruby-900/40 dark:text-ruby-300';
  return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
};

const fmtDay = iso => (iso ? new Date(iso).toLocaleDateString('tr-TR') : '—');

const paymentLabel = r => {
  if (r.payment_status === 'paid') return 'Ödendi';
  if (r.payment_status === 'scheduled')
    return `Planlandı: ${fmtDay(r.payment_date)}`;
  return 'Bekliyor';
};
const paymentClass = r => {
  if (r.payment_status === 'paid')
    return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
  if (r.payment_status === 'scheduled')
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
  return 'bg-n-alpha-black2 text-n-slate-11';
};

const toggleReconcile = async r => {
  const next = !r.reconciled;
  r.reconciled = next; // optimistik
  try {
    await patchMarketplaceJson(accountId, `trendyol_finances/${r.id}`, {
      reconciled: next,
    });
  } catch (e) {
    r.reconciled = !next; // geri al
  }
};

// --- Export + indirme geçmişi ---
const triggerDownload = (content, filename, mime) => {
  const blob = new Blob(['﻿', content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const runExport = async (format, builder) => {
  if (exporting.value) return;
  exporting.value = true;
  showHistory.value = true;
  const entry = reactive({
    id: Date.now(),
    name: `trendyol-finans-${new Date().toISOString().slice(0, 10)}.${format}`,
    status: 'progress',
    at: new Date(),
  });
  downloads.value.unshift(entry);
  try {
    const data = await fetchMarketplaceJson(
      accountId,
      `trendyol_finances/export?${queryString().toString()}`
    );
    builder(data.finances || []);
    entry.status = 'done';
    entry.count = (data.finances || []).length;
  } catch (e) {
    entry.status = 'error';
  } finally {
    exporting.value = false;
  }
};

const buildCsv = data => {
  const head = COLUMNS.map(c => c.label).join(';');
  const lines = data.map(r =>
    [
      fmtDate(r.order_date),
      r.order_number,
      r.customer_name || '',
      r.country || '',
      r.status,
      r.item_count,
      r.order_amount,
      r.commission,
      r.discount,
      r.forward_shipping,
      r.return_shipping,
      r.platform_fee,
      r.return_amount,
      r.net_amount,
      r.product_cost,
      profitKnown(r) ? r.net_profit : 'maliyet eksik',
      paymentLabel(r),
      r.reconciled ? 'Evet' : 'Hayır',
    ]
      .map(v => `"${String(v ?? '').replace(/"/g, '""')}"`)
      .join(';')
  );
  triggerDownload(
    [head, ...lines].join('\r\n'),
    `trendyol-finans-${new Date().toISOString().slice(0, 10)}.csv`,
    'text/csv;charset=utf-8;'
  );
};

const buildExcel = data => {
  const esc = s =>
    String(s ?? '').replace(
      /[&<>]/g,
      c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]
    );
  const head = COLUMNS.map(c => `<th>${esc(c.label)}</th>`).join('');
  const body = data
    .map(
      r =>
        `<tr><td>${fmtDate(r.order_date)}</td><td>${esc(r.order_number)}</td><td>${esc(r.customer_name || '')}</td><td>${esc(r.country || '')}</td><td>${esc(r.status)}</td><td>${r.item_count}</td><td>${r.order_amount}</td><td>${r.commission}</td><td>${r.discount}</td><td>${r.forward_shipping}</td><td>${r.return_shipping}</td><td>${r.platform_fee}</td><td>${r.return_amount}</td><td>${r.net_amount}</td><td>${r.product_cost}</td><td>${profitKnown(r) ? r.net_profit : 'maliyet eksik'}</td><td>${esc(paymentLabel(r))}</td><td>${r.reconciled ? 'Evet' : 'Hayır'}</td></tr>`
    )
    .join('');
  const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
  triggerDownload(
    html,
    `trendyol-finans-${new Date().toISOString().slice(0, 10)}.xls`,
    'application/vnd.ms-excel;charset=utf-8;'
  );
};

const exportCsv = () => runExport('csv', buildCsv);
const exportExcel = () => runExport('xls', buildExcel);

const summaryCards = computed(() => {
  const s = summary.value;
  return [
    { label: 'Sipariş', value: s.count || 0, isCount: true },
    {
      label: 'İade Oranı',
      value: `%${(s.return_rate || 0).toLocaleString('tr-TR')}`,
      isText: true,
      tone: 'minus',
      sub: `${s.returns_count || 0} iade · ${s.cancels_count || 0} iptal`,
    },
    { label: 'Toplam Ciro', value: s.order_amount, tone: 'neutral' },
    { label: 'Komisyon', value: -(s.commission || 0), tone: 'minus' },
    { label: 'İndirim', value: -(s.discount || 0), tone: 'minus' },
    {
      label: 'Gidiş Kargo',
      value: -(s.forward_shipping || 0),
      tone: 'minus',
    },
    {
      label: 'İade Kargo (Toplam)',
      value: -(s.return_shipping || 0),
      tone: 'minus',
      sub: 'iade siparişlerin kargo zararı',
    },
    { label: 'Platform Bedeli', value: -(s.platform_fee || 0), tone: 'minus' },
    { label: 'NET TUTAR', value: s.net_amount, tone: 'net' },
    {
      label: 'Ürün Maliyeti',
      value: -(s.product_cost || 0),
      tone: 'minus',
      sub: `maliyet kapsama %${(s.cost_coverage || 0).toLocaleString('tr-TR')}`,
    },
    {
      label: 'NET KÂR (temkinli)',
      value: s.net_profit,
      tone: 'net',
      sub:
        (s.cost_coverage || 0) < 100
          ? 'maliyetsizler kâr 0 sayıldı, gerçek kâr daha yüksek olabilir'
          : 'ürün maliyeti düşülmüş gerçek kâr',
    },
    {
      label: 'Ödenen (net)',
      value: s.paid_net,
      tone: 'paid',
      sub: `${s.paid_count || 0} sipariş yattı`,
    },
    {
      label: 'Bekleyen (net)',
      value: s.pending_net,
      tone: 'pending',
      sub: 'henüz yatmadı',
    },
  ];
});

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full">
    <div class="flex flex-col gap-4 pt-1">
      <!-- Açıklama -->
      <div class="flex flex-col gap-1">
        <p class="text-sm text-n-slate-11">
          Her siparişin Trendyol tarafından <strong>gerçekleşen</strong>
          komisyon, indirim, gidiş/iade kargo ve net tutarı. İade siparişlerde
          net eksi bakiye; satıra tıklayınca ürün kalemleri ve tam kırılım
          açılır.
        </p>
        <p v-if="meta.synced_at" class="text-xs text-n-slate-10">
          Son güncelleme: {{ fmtDate(meta.synced_at) }} · Toplam
          {{ meta.total }} sipariş
          <span class="text-amber-600 dark:text-amber-400">
            · Çok yeni siparişlerde kargo/komisyon henüz Trendyol tarafından
            faturalanmamış olabilir (settlement bekliyor). İptal edilen
            (sevkiyat öncesi) siparişlerde kargo bedeli olmaz.</span
          >
        </p>
      </div>

      <!-- Filtreler -->
      <div
        class="grid grid-cols-2 gap-3 p-4 rounded-xl md:grid-cols-4 lg:grid-cols-8 bg-n-solid-2 border border-n-weak"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">Mağaza</label>
          <select
            v-model="filters.channel"
            class="px-2 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12"
          >
            <option value="all">Tümü</option>
            <option value="trendyol_koza">Trendyol · Koza</option>
            <option value="trendyol_modax">Trendyol · ModaX</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">Statü</label>
          <select
            v-model="filters.status"
            class="px-2 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12"
          >
            <option value="all">Tümü</option>
            <option value="delivered">Teslim Edildi</option>
            <option value="returned">İade Edildi</option>
            <option value="cancelled">İptal</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">Ödeme</label>
          <select
            v-model="filters.payment"
            class="px-2 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12"
          >
            <option value="all">Tümü</option>
            <option value="paid">Ödendi</option>
            <option value="scheduled">Planlandı (yatmadı)</option>
            <option value="reconciled">Kontrol edildi ✓</option>
            <option value="unreconciled">Kontrol edilmedi</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">Başlangıç</label>
          <input
            v-model="filters.start"
            type="date"
            class="px-2 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">Bitiş</label>
          <input
            v-model="filters.end"
            type="date"
            class="px-2 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12"
          />
        </div>
        <div class="flex flex-col gap-1 lg:col-span-2">
          <label class="text-xs font-medium text-n-slate-11"
            >Sipariş no / müşteri ara</label
          >
          <input
            v-model="filters.q"
            type="text"
            placeholder="Ara..."
            class="px-2 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12"
          />
        </div>
        <div class="flex items-end">
          <button
            class="px-3 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-11 hover:text-n-slate-12 w-full"
            @click="resetFilters"
          >
            Temizle
          </button>
        </div>
      </div>

      <!-- Özet rapor kartları -->
      <div class="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8">
        <div
          v-for="card in summaryCards"
          :key="card.label"
          class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-solid-2 border border-n-weak"
          :class="card.tone === 'net' ? 'ring-1 ring-woot-500' : ''"
        >
          <span class="text-xs font-medium text-n-slate-11">{{
            card.label
          }}</span>
          <span
            class="text-lg font-semibold tabular-nums"
            :class="{
              'text-n-slate-12': !card.tone || card.tone === 'neutral',
              'text-ruby-700 dark:text-ruby-400': card.tone === 'minus',
              'text-green-700 dark:text-green-400': card.tone === 'paid',
              'text-blue-700 dark:text-blue-400': card.tone === 'pending',
              [netClass(card.value)]: card.tone === 'net',
            }"
          >
            {{ card.isCount || card.isText ? card.value : money(card.value) }}
          </span>
          <span v-if="card.sub" class="text-[11px] text-n-slate-10">{{
            card.sub
          }}</span>
        </div>
      </div>

      <!-- Export + sonuç sayısı -->
      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="text-sm text-n-slate-11">
          {{ pagination.total }} sipariş bulundu
        </span>
        <div class="relative flex items-center gap-2">
          <button
            :disabled="exporting"
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            @click="exportExcel"
          >
            <span class="i-lucide-file-spreadsheet" /> Excel indir
          </button>
          <button
            :disabled="exporting"
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12 disabled:opacity-50"
            @click="exportCsv"
          >
            <span class="i-lucide-download" /> CSV indir
          </button>
          <button
            class="flex items-center justify-center w-8 h-8 rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-11 hover:text-n-slate-12"
            title="İndirme geçmişim"
            @click="showHistory = !showHistory"
          >
            <span
              class="i-lucide-chevron-down transition-transform"
              :class="showHistory ? 'rotate-180' : ''"
            />
          </button>

          <!-- İndirme geçmişi kutucuğu -->
          <div
            v-if="showHistory"
            class="absolute right-0 top-10 z-20 w-72 p-3 rounded-xl bg-n-background border border-n-weak shadow-lg"
          >
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-medium text-n-slate-12"
                >İndirme Geçmişim</span
              >
              <button
                class="text-n-slate-10 hover:text-n-slate-12"
                @click="showHistory = false"
              >
                <span class="i-lucide-x" />
              </button>
            </div>
            <p
              v-if="!downloads.length"
              class="py-3 text-xs text-center text-n-slate-10"
            >
              Henüz indirme yok. Excel veya CSV indir.
            </p>
            <ul v-else class="flex flex-col gap-2 max-h-64 overflow-auto">
              <li
                v-for="d in downloads"
                :key="d.id"
                class="flex items-center gap-2 text-sm"
              >
                <span
                  v-if="d.status === 'progress'"
                  class="w-4 h-4 rounded-full border-2 border-n-slate-6 border-t-woot-500 animate-spin"
                />
                <span
                  v-else-if="d.status === 'done'"
                  class="i-lucide-check-circle text-green-600"
                />
                <span v-else class="i-lucide-x-circle text-ruby-600" />
                <span class="flex-1 truncate text-n-slate-12">{{
                  d.name
                }}</span>
                <span class="text-[11px] text-n-slate-10">
                  {{
                    d.status === 'progress'
                      ? 'indiriliyor...'
                      : d.status === 'done'
                        ? `${d.count} satır`
                        : 'hata'
                  }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <p v-if="errorMsg" class="text-sm text-ruby-700 dark:text-ruby-400">
        {{ errorMsg }}
      </p>

      <!-- Tablo -->
      <div class="overflow-x-auto rounded-xl border border-n-weak">
        <table class="w-full text-sm border-collapse min-w-[1100px]">
          <thead>
            <tr class="bg-n-solid-2 text-n-slate-11">
              <th
                v-for="col in COLUMNS"
                :key="col.key"
                class="px-3 py-2 font-medium text-left whitespace-nowrap"
                :class="MONEY_KEYS.includes(col.key) ? 'text-right' : ''"
              >
                {{ col.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td
                :colspan="COLUMNS.length"
                class="px-3 py-8 text-center text-n-slate-10"
              >
                Yükleniyor...
              </td>
            </tr>
            <tr v-else-if="!rows.length">
              <td
                :colspan="COLUMNS.length"
                class="px-3 py-8 text-center text-n-slate-10"
              >
                Kayıt bulunamadı.
              </td>
            </tr>
            <tr
              v-for="r in rows"
              v-else
              :key="r.id"
              class="border-t border-n-weak cursor-pointer hover:bg-n-alpha-black1"
              :class="r.returned ? 'bg-ruby-50 dark:bg-ruby-900/10' : ''"
              @click="openDetail(r)"
            >
              <td class="px-3 py-2 whitespace-nowrap text-n-slate-12">
                {{ fmtDate(r.order_date) }}
              </td>
              <td class="px-3 py-2 font-medium text-n-slate-12">
                {{ r.order_number }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">
                {{ r.customer_name || '—' }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">{{ r.country || '—' }}</td>
              <td class="px-3 py-2">
                <span
                  class="px-2 py-0.5 text-xs rounded-full"
                  :class="statusClass(r)"
                >
                  {{ r.status }}
                </span>
              </td>
              <td class="px-3 py-2 text-center text-n-slate-11">
                {{ r.item_count }}
              </td>
              <td class="px-3 py-2 text-right tabular-nums text-n-slate-12">
                {{ money(r.order_amount) }}
              </td>
              <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                {{ r.commission ? money(-r.commission) : '—' }}
              </td>
              <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                {{ r.discount ? money(-r.discount) : '—' }}
              </td>
              <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                {{ r.forward_shipping ? money(-r.forward_shipping) : '—' }}
              </td>
              <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                {{ r.return_shipping ? money(-r.return_shipping) : '—' }}
              </td>
              <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                {{ r.platform_fee ? money(-r.platform_fee) : '—' }}
              </td>
              <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                {{ r.return_amount ? money(-r.return_amount) : '—' }}
              </td>
              <td
                class="px-3 py-2 text-right tabular-nums"
                :class="netClass(r.net_amount)"
              >
                {{ money(r.net_amount) }}
              </td>
              <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                {{ r.product_cost ? money(-r.product_cost) : '—' }}
              </td>
              <td class="px-3 py-2 text-right tabular-nums">
                <span v-if="profitKnown(r)" :class="netClass(r.net_profit)">
                  {{ money(r.net_profit) }}
                </span>
                <span
                  v-else
                  class="text-n-slate-10"
                  title="Maliyet girilmemiş — kâr 0 (maliyetine satıldı) sayıldı"
                >
                  {{ money(0) }} <span class="text-[10px]">·maliyetsiz</span>
                </span>
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                <span
                  class="px-2 py-0.5 text-xs rounded-full"
                  :class="paymentClass(r)"
                >
                  {{ paymentLabel(r) }}
                </span>
              </td>
              <td class="px-3 py-2 text-center" @click.stop>
                <input
                  type="checkbox"
                  :checked="r.reconciled"
                  class="w-4 h-4 cursor-pointer accent-woot-500"
                  title="Banka ekstresinde kontrol ettim"
                  @change="toggleReconcile(r)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Sayfalama -->
      <div
        v-if="pagination.total_pages > 1"
        class="flex items-center justify-center gap-2 py-2"
      >
        <button
          class="px-3 py-1.5 text-sm rounded-lg bg-n-solid-2 border border-n-weak text-n-slate-12 disabled:opacity-40"
          :disabled="pagination.page <= 1"
          @click="goToPage(pagination.page - 1)"
        >
          Önceki
        </button>
        <span class="text-sm text-n-slate-11">
          Sayfa {{ pagination.page }} / {{ pagination.total_pages }}
        </span>
        <button
          class="px-3 py-1.5 text-sm rounded-lg bg-n-solid-2 border border-n-weak text-n-slate-12 disabled:opacity-40"
          :disabled="pagination.page >= pagination.total_pages"
          @click="goToPage(pagination.page + 1)"
        >
          Sonraki
        </button>
      </div>
    </div>

    <!-- Detay modalı -->
    <div
      v-if="selected"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      @click.self="selected = null"
    >
      <div
        class="flex flex-col w-full max-w-xl gap-4 p-6 rounded-2xl bg-n-background border border-n-weak max-h-[90vh] overflow-auto"
      >
        <div class="flex items-start justify-between">
          <div>
            <h2 class="text-xl font-semibold text-n-slate-12">
              Sipariş {{ selected.order_number }}
            </h2>
            <p class="text-sm text-n-slate-11">
              {{ fmtDate(selected.order_date) }} ·
              {{ selected.channel_label }} · {{ selected.country }}
            </p>
          </div>
          <button
            class="text-n-slate-10 hover:text-n-slate-12"
            @click="selected = null"
          >
            <span class="i-lucide-x text-xl" />
          </button>
        </div>

        <span
          class="self-start px-3 py-1 text-sm rounded-full"
          :class="statusClass(selected)"
        >
          {{ selected.status }}
        </span>

        <!-- Ürün kalemleri -->
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12"
            >Ürünler ({{ selected.item_count }} kalem)</span
          >
          <p
            v-if="loadingProducts"
            class="py-3 text-sm text-center text-n-slate-10"
          >
            Ürünler yükleniyor...
          </p>
          <p
            v-else-if="!selectedProducts.length"
            class="py-2 text-xs text-n-slate-10"
          >
            Bu siparişin ürün detayı henüz veritabanında yok (eski sipariş —
            sipariş senkronu tamamlanınca görünecek).
          </p>
          <div
            v-for="(p, i) in selectedProducts"
            v-else
            :key="i"
            class="flex items-center gap-3 p-2 rounded-lg bg-n-solid-2 border border-n-weak"
          >
            <img
              v-if="p.image"
              :src="p.image"
              :alt="p.name"
              class="object-cover w-12 h-12 rounded-md bg-n-alpha-black2"
            />
            <div
              v-else
              class="flex items-center justify-center w-12 h-12 rounded-md bg-n-alpha-black2 text-n-slate-10"
            >
              <span class="i-lucide-shirt" />
            </div>
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm truncate text-n-slate-12">{{
                p.name || p.product_code
              }}</span>
              <span class="text-xs text-n-slate-10">
                {{ p.product_code
                }}<template v-if="p.color"> · {{ p.color }}</template
                ><template v-if="p.size"> · {{ p.size }}</template>
              </span>
            </div>
            <div class="text-right">
              <div class="text-sm text-n-slate-12 tabular-nums">
                {{ p.quantity }} ad · {{ money(p.amount) }}
              </div>
              <div
                v-if="p.line_cost != null"
                class="text-xs text-n-slate-10 tabular-nums"
              >
                maliyet {{ money(p.line_cost) }} · brüt
                <span :class="netClass(p.gross_profit)">{{
                  money(p.gross_profit)
                }}</span>
              </div>
              <div v-else class="text-xs text-amber-600 dark:text-amber-400">
                maliyet girilmemiş
              </div>
            </div>
          </div>
        </div>

        <!-- Finansal kırılım -->
        <div class="flex flex-col gap-2 text-sm">
          <div class="flex justify-between py-1.5 border-b border-n-weak">
            <span class="text-n-slate-11">Müşteri</span>
            <span class="text-n-slate-12">{{
              selected.customer_name || '—'
            }}</span>
          </div>
          <div class="flex justify-between py-1.5 border-b border-n-weak">
            <span class="text-n-slate-11">Sipariş Tutarı</span>
            <span class="font-medium text-n-slate-12 tabular-nums">{{
              money(selected.order_amount)
            }}</span>
          </div>
          <div class="flex justify-between py-1.5 border-b border-n-weak">
            <span class="text-n-slate-11">Komisyon</span>
            <span class="text-ruby-700 dark:text-ruby-400 tabular-nums">{{
              money(-selected.commission)
            }}</span>
          </div>
          <div class="flex justify-between py-1.5 border-b border-n-weak">
            <span class="text-n-slate-11">İndirim</span>
            <span class="text-ruby-700 dark:text-ruby-400 tabular-nums">{{
              money(-selected.discount)
            }}</span>
          </div>
          <div class="flex justify-between py-1.5 border-b border-n-weak">
            <span class="text-n-slate-11">Gidiş Kargo Bedeli</span>
            <span class="text-ruby-700 dark:text-ruby-400 tabular-nums">{{
              money(-selected.forward_shipping)
            }}</span>
          </div>
          <div
            v-if="selected.returned"
            class="flex justify-between py-1.5 border-b border-n-weak"
          >
            <span class="text-n-slate-11">İade Kargo Bedeli</span>
            <span class="text-ruby-700 dark:text-ruby-400 tabular-nums">{{
              money(-selected.return_shipping)
            }}</span>
          </div>
          <div class="flex justify-between py-1.5 border-b border-n-weak">
            <span class="text-n-slate-11"
              >Platform Hizmet Bedeli (dağıtılmış)</span
            >
            <span class="text-ruby-700 dark:text-ruby-400 tabular-nums">{{
              money(-selected.platform_fee)
            }}</span>
          </div>
          <div
            v-if="selected.return_amount"
            class="flex justify-between py-1.5 border-b border-n-weak"
          >
            <span class="text-n-slate-11">İade (sipariş tutarı geri)</span>
            <span class="text-ruby-700 dark:text-ruby-400 tabular-nums">{{
              money(-selected.return_amount)
            }}</span>
          </div>
          <div class="flex justify-between py-1.5 border-b border-n-weak">
            <span class="text-base font-semibold text-n-slate-12"
              >Net Tutar</span
            >
            <span
              class="text-base tabular-nums"
              :class="netClass(selected.net_amount)"
              >{{ money(selected.net_amount) }}</span
            >
          </div>
          <div
            v-if="!selected.return_amount"
            class="flex justify-between py-1.5 border-b border-n-weak"
          >
            <span class="text-n-slate-11">Ürün Maliyeti</span>
            <span
              v-if="selected.cost_complete"
              class="text-ruby-700 dark:text-ruby-400 tabular-nums"
              >{{ money(-selected.product_cost) }}</span
            >
            <span
v-else class="text-amber-600 dark:text-amber-400 text-xs"
              >girilmemiş → maliyetine satıldı sayıldı</span
            >
          </div>
          <div class="flex justify-between py-2 mt-1">
            <span class="text-base font-bold text-n-slate-12">Net Kâr</span>
            <span
              class="text-lg font-bold tabular-nums"
              :class="
                profitKnown(selected)
                  ? netClass(selected.net_profit)
                  : 'text-n-slate-10'
              "
              >{{ money(selected.net_profit) }}</span
            >
          </div>
          <p
            v-if="!profitKnown(selected) && !selected.return_amount"
            class="text-[11px] text-n-slate-10"
          >
            Bu siparişin ürün maliyeti girilmediği için kâr 0 (maliyetine
            satıldı) sayıldı — maliyet girilince gerçek kâr görünecek.
          </p>
        </div>

        <!-- Ödeme durumu + kontrol -->
        <div
          class="flex flex-col gap-2 p-3 rounded-lg bg-n-solid-2 border border-n-weak"
        >
          <div class="flex items-center justify-between">
            <span class="text-sm text-n-slate-11">Ödeme Durumu</span>
            <span
              class="px-2 py-0.5 text-xs rounded-full"
              :class="paymentClass(selected)"
              >{{ paymentLabel(selected) }}</span
            >
          </div>
          <div
            v-if="selected.payment_date"
            class="flex items-center justify-between text-xs text-n-slate-10"
          >
            <span>{{
              selected.paid ? 'Ödeme tarihi' : 'Planlanan ödeme'
            }}</span>
            <span>{{ fmtDay(selected.payment_date) }}</span>
          </div>
          <div
            v-if="selected.payment_order_id"
            class="flex items-center justify-between text-xs text-n-slate-10"
          >
            <span>Ödeme dekont no</span>
            <span>{{ selected.payment_order_id }}</span>
          </div>
          <label
            class="flex items-center gap-2 pt-1 mt-1 text-sm cursor-pointer border-t border-n-weak"
          >
            <input
              type="checkbox"
              :checked="selected.reconciled"
              class="w-4 h-4 cursor-pointer accent-woot-500"
              @change="toggleReconcile(selected)"
            />
            <span class="text-n-slate-12">Banka ekstresinde kontrol ettim</span>
          </label>
        </div>

        <p
          v-if="selected.returned"
          class="text-xs text-n-slate-10 p-2 rounded-lg bg-ruby-50 dark:bg-ruby-900/10"
        >
          Bu sipariş iade edildiği için net tutar eksi bakiyedir: kargo
          masrafları (gidiş + iade) sende kalır, sipariş tutarı geri alınır.
          Ürün maliyeti düşülmez çünkü iade gelen mal stoğa geri döner.
        </p>
        <p
          v-else-if="selected.cancelled"
          class="text-xs text-n-slate-10 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/10"
        >
          Bu sipariş sevkiyat öncesi iptal edildiği için kargolanmadı — kargo ve
          komisyon bedeli oluşmaz.
        </p>
      </div>
    </div>
  </div>
</template>
